/**
 * AboutEditorPanel — dedicated editor for the `about` section kind (Phase 11.6.3). The
 * Manual Editor sidebar labels this section "Content" (see `SECTION_KIND_LABEL.about` in
 * editorContext.ts, the single source for that rename) — the SiteSchema kind, registry
 * key, runtime, AI generator, component folder, and file name all keep using `"about"`.
 * Only this screen's user-facing text changed to match.
 *
 * Exposes exactly the controls the production About component (`site-builder/components/about/About.tsx`, a literal
 * clone of `__reference__/realDemo/landing/components/content`) actually renders: heading,
 * description, image, layout, visibility, and (when present) an action button —
 * the reference "content" row passes `actions` through to the same `LandingViewInfo`/
 * `InfoBlock` primitive the Banner uses, so it is real, not invented, but the generator
 * never populates it for About, hence it's shown only when an action already exists.
 *
 * No Eyebrow control — as with the Banner, the shared `InfoBlock` renders only a decorative
 * bar, never eyebrow text (see `shared/blocks.tsx`), so an Eyebrow field would edit nothing.
 * No Items control — although the registry declares `items: true` for `about`, `About.tsx`
 * never reads `content.items`; exposing one would silently do nothing.
 *
 * Layout/Alignment UI simplification: the schema/registry still carry THREE `about` layouts
 * (`card` | `cardRight` | `cover`, see registerBuiltins.ts) — unchanged, so previously saved
 * `cardRight` sections keep rendering exactly as before (the "Card (reversed)" CSS in
 * about.css is untouched). Only this editor's presentation changed: instead of a 3-way
 * Layout selector, `cardRight` is folded into a "Card" Layout + an "Alignment" (Left/Right)
 * control that's shown only for Card — Right writes `layout: 'cardRight'`, Left writes
 * `layout: 'card'`, both through the SAME `setSectionLayout` setter every other section
 * already uses, so Live Preview updates exactly as it does for any other layout change.
 */
import type { LayoutId } from '../../site-builder/schema'
import { builderRegistry } from '../previewRuntime'
import { useBuilderEditor, assetUrl } from './editorContext'
import { Fieldset, SegmentedControl, TextField, TextArea, ToggleField, ImageUploadField } from './controls'

type AboutUiLayout = 'card' | 'cover'
type CardAlignment = 'left' | 'right'

export function AboutEditorPanel({ sectionId }: { sectionId: string }) {
  const e = useBuilderEditor()
  const section = e.schema.pages[0]?.sections.find((s) => s.id === sectionId)
  if (!section) {
    return <p className="px-5 py-6 text-xs text-gray-400">This section is no longer available.</p>
  }

  const layouts = (builderRegistry.entry('about')?.supportedLayouts ?? []) as readonly LayoutId[]
  const actions = section.content.actions ?? []
  const imageValue = assetUrl(e.schema, section.content.media?.[0]?.assetId) || undefined

  // `cardRight` is a real, still-supported schema layout — it's just presented here as
  // Card + Alignment: Right instead of its own Layout option (see header comment).
  const uiLayout: AboutUiLayout = section.layout === 'cover' ? 'cover' : 'card'
  const alignment: CardAlignment = section.layout === 'cardRight' ? 'right' : 'left'

  return (
    <div>
      <Fieldset title="Visibility">
        <ToggleField
          label="Show Content"
          description="Hide to remove it from the published site"
          checked={section.visible}
          onChange={() => e.toggleSectionVisible(sectionId)}
        />
      </Fieldset>

      <Fieldset title="Image">
        <ImageUploadField
          label="Image"
          value={imageValue}
          onChange={(src) => e.setSectionImage(sectionId, src)}
        />
      </Fieldset>

      <Fieldset title="Content">
        <TextField label="Heading" value={section.content.heading ?? ''} onChange={(v) => e.setSectionText(sectionId, 'heading', v)} />
        <TextArea label="Description" value={section.content.subheading ?? ''} rows={4} onChange={(v) => e.setSectionText(sectionId, 'subheading', v)} />
      </Fieldset>

      {layouts.length > 1 ? (
        <Fieldset title="Layout">
          <SegmentedControl<AboutUiLayout>
            label="Layout"
            value={uiLayout}
            options={[
              { value: 'card', label: 'Card' },
              { value: 'cover', label: 'Cover' },
            ]}
            onChange={(v) => e.setSectionLayout(sectionId, v === 'cover' ? 'cover' : alignment === 'right' ? 'cardRight' : 'card')}
          />
          {uiLayout === 'card' ? (
            <SegmentedControl<CardAlignment>
              label="Alignment"
              value={alignment}
              options={[
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
              ]}
              onChange={(v) => e.setSectionLayout(sectionId, v === 'right' ? 'cardRight' : 'card')}
            />
          ) : null}
        </Fieldset>
      ) : null}

      {actions.length > 0 ? (
        <Fieldset title="Actions">
          {actions.map((a) => (
            <div key={a.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
              <TextField label="Label" value={a.label} onChange={(v) => e.setActionField(sectionId, a.id, 'label', v)} />
              <TextField label="Link" value={a.value ?? ''} onChange={(v) => e.setActionField(sectionId, a.id, 'value', v)} />
            </div>
          ))}
        </Fieldset>
      ) : null}
    </div>
  )
}
